/**
 * "Only mock-ups" — the finished images of Generate's "Mock-up only" runs. One card per view,
 * grouped by the run that made them. The Details are labels only: editing them never changes
 * an image. Export saves the chosen runs' images beside the Mock-ups sheet, one row per run.
 *
 * A customer reads their own jobs; an admin reads the admin list (their tenant, or every tenant
 * for a super-admin), with the owner on each run. The single-job GET is the owner's only, so an
 * admin's rows for other people are refreshed by reading the list again.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GenerationJobView, TemplateOptions } from "@genz/platform";
import { useServices } from "../app/context.js";
import { useSessionStore } from "../stores/session.js";
import { ActionBar } from "../ui/ActionBar.js";
import { BusyButton } from "../ui/BusyButton.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { EmptyState } from "../ui/EmptyState.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { Modal } from "../ui/Modal.js";
import { RowAction } from "../ui/RowAction.js";
import { InfoHint } from "../ui/Tooltip.js";
import { ProductPickers } from "../wizard/ProductPickers.js";
import { BaseTypePicker, StylePicker, useCaseStyles } from "../wizard/DetailsPickers.js";
import { usePhoneCatalog } from "../wizard/usePhoneCatalog.js";
import { fetchResultBytes, isOngoing, isReady, isTerminal, jobBadge, jobLabel, useDocumentVisible, useGenerationJobs } from "../generate/candidates.js";
import { useExpiredLinkRefresh } from "../generate/generationIndex.js";
import { refusalOf, refusalText } from "../generate/jobActions.js";
import { errorDetail } from "../generate/errorCode.js";
import { destLabel, exportBundle, exportProblemSummary } from "../export/bundle.js";
import { toCsvBytes, toXlsxBytes } from "../export/sheet.js";
import type { MockupWizardDetails } from "../stores/wizard.js";
import { exportPlan, extOf, groupRuns, runLabel, templateDetails, viewLabel, type MockupRun, type RunImages } from "./mockupRuns.js";
import { ALL_VIEWS } from "../templates/views.js";
import "../generate/generate.css";

/** The own list's largest page. */
const LIST_LIMIT = 200;

/** A job the cloud will relabel: mockup-only and no longer running. */
const settled = (job: GenerationJobView) => !isOngoing(job);
/** A card on the page: an image, one on its way, or one that failed or is held. */
const onPage = (job: GenerationJobView) => isReady(job) || isOngoing(job) || job.status === "failed" || job.status === "held";

/** Only the admin list sends the owner, so a job read any other way keeps the one shown. */
const keepOwner = (shown: GenerationJobView, next: GenerationJobView): GenerationJobView =>
  ({ ...next, ownerEmail: next.ownerEmail ?? shown.ownerEmail });

type Notice = { detail: string; reference?: string };

export interface MockupsOnlyPageProps {
  /** False while the page stays mounted but another route is on screen: polling pauses. */
  active?: boolean;
  /** Super-admin: open the template wizard on a run's image and Details. Hidden while absent. */
  onMakeTemplate?: (image: { bytes: Uint8Array; mediaType: string }, details: MockupWizardDetails) => void;
}

export function MockupsOnlyPage({ active = true, onMakeTemplate }: MockupsOnlyPageProps) {
  const { client, io } = useServices();
  const admin = useSessionStore((s) => s.isAdmin());
  const superAdmin = useSessionStore((s) => s.isSuperAdmin());
  const me = useSessionStore((s) => s.session?.customerId);
  const { styles } = useCaseStyles();
  const catalog = usePhoneCatalog();

  const [rows, setRows] = useState<GenerationJobView[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Notice | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [editing, setEditing] = useState<MockupRun | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const jobs = admin
        ? await client.adminListGenerationJobs({ output: "mockup" })
        : (await client.listGenerationJobs({ limit: LIST_LIMIT, output: "mockup" })).jobs;
      setRows(jobs);
      setError(null);
      return jobs;
    } catch (e) {
      setError({ detail: errorDetail(e) });
      return [];
    } finally {
      setLoaded(true);
    }
  }, [admin, client]);
  useEffect(() => { void load(); }, [load]);

  const replace = (jobs: GenerationJobView[]) => setRows((prev) => {
    const byId = new Map(jobs.map((j) => [j.id, j]));
    return prev.map((j) => {
      const next = byId.get(j.id);
      return next ? keepOwner(j, next) : j;
    });
  });
  const drop = (id: string) => setRows((prev) => prev.filter((j) => j.id !== id));

  /** The job with its links signed afresh: the owner asks for it; an admin reads the list again. */
  const fresh = async (job: GenerationJobView): Promise<GenerationJobView | undefined> => {
    if (job.customerId !== me) return (await load()).find((j) => j.id === job.id);
    const next = await client.getGenerationJob(job.id);
    replace([next]);
    return next;
  };
  const expiredLink = useExpiredLinkRefresh(async (id) => {
    const job = rows.find((j) => j.id === id);
    if (job) await fresh(job);
  });

  const visible = useDocumentVisible();
  const polled = useMemo(() => rows.filter((j) => !isTerminal(j) && j.customerId === me).map((j) => j.id), [rows, me]);
  const { snapshots } = useGenerationJobs(client, polled, { paused: !active || !visible, onGone: drop });
  const runs = useMemo(() => groupRuns(rows
    .map((row) => (isTerminal(row) || !snapshots[row.id] ? row : keepOwner(row, snapshots[row.id])))
    // An older cloud ignores `output` and lists every job.
    .filter((j) => j.mockupOnly && onPage(j))), [rows, snapshots]);

  const styleLabel = (id: string | null) => styles.find((s) => s.id === id)?.label ?? "";
  const exportable = (run: MockupRun) => run.jobs.filter((j) => isReady(j));
  const chosen = runs.filter((r) => selected.has(r.runId) && exportable(r).length > 0);
  const toggle = (runId: string, on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(runId);
    else next.delete(runId);
    return next;
  });

  const act = async (job: GenerationJobView, request: () => Promise<void>) => {
    setActing(job.id);
    setError(null);
    try {
      await request();
    } catch (e) {
      if (refusalOf(e) === "gone") drop(job.id);
      else setError({ detail: refusalText(e, { admin, reference: job.id }), reference: job.id });
    } finally {
      setActing(null);
    }
  };
  const remove = (job: GenerationJobView) => act(job, async () => {
    await client.removeGenerationJob(job.id);
    drop(job.id);
  });
  // A personal-account (device) job is run by the computer that made it; the page cannot run it.
  // A held job may already be paid for.
  const mayRegenerate = (job: GenerationJobView) => job.customerId === me && settled(job) && job.status !== "held"
    && job.executor !== "device";
  const regenerate = (job: GenerationJobView) => act(job, async () => {
    const made = await client.regenerateGenerationJob(job.id);
    setRows((prev) => [made, ...prev]);
  });

  const imageBytes = async (job: GenerationJobView) => {
    const download = io.downloadBytes?.bind(io);
    try {
      return await fetchResultBytes(job.resultUrl ?? "", download);
    } catch {
      // A link signed when the list was read may have expired: ask for a fresh one, once.
      return fetchResultBytes((await fresh(job))?.resultUrl ?? "", download);
    }
  };

  /** The wizard handles one image: the run's back view, else its first finished one. */
  const makeTemplate = async (run: MockupRun) => {
    const ready = exportable(run);
    const job = ready.find((j) => j.view === "back") ?? ready[0];
    if (!job || !onMakeTemplate) return;
    setError(null);
    try {
      onMakeTemplate(await imageBytes(job), templateDetails(run, job, catalog.models));
    } catch (e) {
      setError({ detail: errorDetail(e), reference: job.id });
    }
  };

  const exportSelected = async () => {
    setStatus(null);
    setError(null);
    const withImages: RunImages[] = [];
    for (const run of chosen) {
      const images: RunImages["images"] = [];
      for (const job of exportable(run)) {
        try {
          const { bytes, mediaType } = await imageBytes(job);
          images.push({ job, bytes, ext: extOf(mediaType) });
        } catch (e) {
          setError({ detail: errorDetail(e), reference: job.id });
          return;
        }
      }
      withImages.push({ run, images });
    }
    const { files, rows: sheetRows, notInSheet } = exportPlan(withImages, styleLabel);
    const report = await exportBundle(io, {
      name: "Only mock-ups",
      files: [...files, { relPath: "export-sheet.xlsx", bytes: toXlsxBytes(sheetRows) }],
      sheet: { name: "export-sheet.csv", bytes: toCsvBytes(sheetRows) },
    });
    if (report.cancelled) {
      setStatus("Export cancelled — no folder chosen, nothing was written.");
      return;
    }
    const plural = (n: number) => `${n} image${n === 1 ? "" : "s"}`;
    const outside = notInSheet
      ? `. ${plural(notInSheet)} ${notInSheet === 1 ? "is" : "are"} not in the sheet: it has ${ALL_VIEWS.length} view columns.`
      : "";
    setStatus(`Saved ${plural(files.length)} and the sheet ` +
      `${destLabel(report, io.kind)}${outside}${exportProblemSummary(report)}`);
  };

  const preview = runs.flatMap((r) => r.jobs).find((j) => j.id === previewId);

  return (
    <section className="generate-panel mockups-only" data-testid="mockups-only-page">
      <header className="generate-panel__head">
        <h1 className="generate-panel__title panel-title">
          Only mock-ups <InfoHint text="The images of Generate's Mock-up only runs. Label them, then export the images with a sheet." />
        </h1>
      </header>
      {error && (
        <p className="banner banner--danger" role="alert" data-testid="mockups-only-error">
          <ErrorNotice detail={error.detail} reference={error.reference} />
        </p>
      )}
      {loaded && runs.length === 0 ? (
        <EmptyState icon="image" title="No Mock-up only images yet"
          hint="On Generate, set Output to Mock-up only. The images collect here." testId="mockups-only-empty" />
      ) : (
        <ul className="mockups-only__runs" data-testid="mockups-only-runs">
          {runs.map((run) => {
            const label = runLabel(run);
            const sub = [run.details.brand, run.details.model, styleLabel(run.details.caseStyleId)].filter(Boolean).join(" · ");
            const owner = run.jobs[0].ownerEmail;
            return (
              <li key={run.runId} className="mockups-only__run" data-testid="mockup-run" data-run={run.runId}>
                <div className="mockups-only__run-head">
                  <input type="checkbox" checked={selected.has(run.runId)} disabled={exportable(run).length === 0}
                    onChange={(e) => toggle(run.runId, e.target.checked)} aria-label={`Select ${label}`}
                    data-testid={`mockup-run-select-${run.runId}`} />
                  <span className="mockups-only__run-title" data-testid={`mockup-run-title-${run.runId}`}>
                    <strong>{label}</strong>
                    {sub && <small className="faint">{sub}</small>}
                  </span>
                  {admin && owner && <span className="caption" data-testid="mockup-run-owner">{owner}</span>}
                  <RowAction label={`Edit details: ${label}`} hint="Edit the name, phone, style and base type" icon="editor"
                    tone="quiet" onClick={() => setEditing(run)} data-testid={`mockup-run-edit-${run.runId}`} />
                  {superAdmin && onMakeTemplate && (
                    <RowAction label={`Make template from ${label}`} icon="templates" onClick={() => void makeTemplate(run)}
                      disabled={exportable(run).length === 0} data-testid={`mockup-run-make-template-${run.runId}`} />
                  )}
                </div>
                <ul className="mockups-only__cards">
                  {run.jobs.map((job) => (
                    <MockupCard key={job.id} job={job} busy={acting === job.id}
                      onOpen={() => setPreviewId(job.id)}
                      onRegenerate={mayRegenerate(job) ? () => void regenerate(job) : undefined}
                      onRemove={() => void remove(job)}
                      onImageError={() => expiredLink.onError(job.id)} onImageLoad={() => expiredLink.onLoad(job.id)} />
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
      <ActionBar label="Only mock-ups actions" primary={
        <BusyButton className="btn btn--primary" onAction={exportSelected} disabled={chosen.length === 0}
          title={chosen.length === 0 ? "Tick the runs to export" : undefined} data-testid="mockups-only-export">
          Export {chosen.length} selected
        </BusyButton>
      } />
      {status && <p className="caption mockups-only__status" role="status" data-testid="mockups-only-status">{status}</p>}
      {preview?.resultUrl && (
        <Modal testid="mockup-preview-modal" title={viewLabel(preview.view)} onClose={() => setPreviewId(null)}>
          <img className="generate-panel__preview-img" src={preview.resultUrl} alt={`Mockup: ${viewLabel(preview.view)}`}
            data-testid="mockup-preview-img" onError={() => expiredLink.onError(preview.id)}
            onLoad={() => expiredLink.onLoad(preview.id)} />
        </Modal>
      )}
      {editing && (
        <EditDetails run={editing} admin={admin} onClose={() => setEditing(null)} onSaved={replace} />
      )}
    </section>
  );
}

function MockupCard({ job, busy, onOpen, onRegenerate, onRemove, onImageError, onImageLoad }: {
  job: GenerationJobView;
  busy: boolean;
  onOpen: () => void;
  onRegenerate?: () => void;
  onRemove: () => void;
  onImageError: () => void;
  onImageLoad: () => void;
}) {
  const view = viewLabel(job.view);
  const b = jobBadge(job);
  return (
    <li className="mockups-only__card" data-testid="mockup-card" data-job={job.id}>
      {isReady(job) && job.resultUrl ? (
        <button type="button" className="mockups-only__card-open" onClick={onOpen} aria-label={`Preview ${view}`}
          data-testid={`mockup-card-open-${job.id}`}>
          <img className="mockups-only__card-img" src={job.resultUrl} alt={`Mockup: ${view}`}
            onError={onImageError} onLoad={onImageLoad} />
        </button>
      ) : (
        <div className="mockups-only__card-img mockups-only__card-img--empty" aria-hidden="true" />
      )}
      <div className="mockups-only__card-foot">
        <span className="mockups-only__card-view">{view}</span>
        {!isReady(job) && <span className={`badge badge--plain badge--${b.variant}`}>{jobLabel(job)}</span>}
        {onRegenerate && (
          <ConfirmPopover message={`Regenerate ${view}? It is one more paid generation. This image stays.`}
            confirmLabel="Regenerate" onConfirm={onRegenerate}>
            {(toggle) => (
              <RowAction label={`Regenerate ${view}`} icon="refresh" onClick={toggle} disabled={busy}
                data-testid={`mockup-card-regenerate-${job.id}`} />
            )}
          </ConfirmPopover>
        )}
        <ConfirmPopover message="Remove this image from the list? The stored file stays." confirmLabel="Remove" danger
          onConfirm={onRemove}>
          {(toggle) => (
            <RowAction label={`Remove ${view}`} hint="Remove this image" icon="minus-circle" tone="quiet" onClick={toggle}
              disabled={busy} busy={busy} data-testid={`mockup-card-remove-${job.id}`} />
          )}
        </ConfirmPopover>
      </div>
    </li>
  );
}

/** The run's Details, with Generate's pickers. Saved to every settled view of the run. */
function EditDetails({ run, admin, onClose, onSaved }: {
  run: MockupRun;
  admin: boolean;
  onClose: () => void;
  /** Merges the views saved so far; the dialog closes only when every view saved. */
  onSaved: (jobs: GenerationJobView[]) => void;
}) {
  const { client } = useServices();
  const catalog = usePhoneCatalog();
  const d = run.details;
  const [productType, setProductType] = useState(d.productType ?? "");
  const [brand, setBrand] = useState(d.brand ?? "");
  const [modelId, setModelId] = useState(d.phoneModelId ?? "");
  const [caseStyleId, setCaseStyleId] = useState(d.caseStyleId ?? "");
  const [baseType, setBaseType] = useState(d.baseType ?? "");
  const [name, setName] = useState(d.name ?? "");
  const [types, setTypes] = useState<NonNullable<TemplateOptions["product_types"]>>([]);
  const [error, setError] = useState<Notice | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    client.templateOptions().then((o) => setTypes(o.product_types ?? []), () => undefined);
  }, [client]);
  // An older job has no catalog link: its brand and model words find the model.
  useEffect(() => {
    if (modelId || !d.model) return;
    const same = (a?: string | null, b?: string | null) => (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
    const found = catalog.models.find((m) => same(m.brand, d.brand) && same(m.model, d.model));
    if (found) setModelId(found.id);
  }, [catalog.models, modelId, d.brand, d.model]);

  const phoneModel = catalog.models.find((m) => m.id === modelId);
  // Only a type with a clear pass may label a mock-up; the run's own type stays listed.
  const listed = types.filter((t) => t.mockup_only || t.code === d.productType);
  const categories = listed.length ? listed.map((t) => ({ value: t.code, label: t.label }))
    : d.productType ? [{ value: d.productType, label: d.productType }] : [];

  const save = async () => {
    if (!phoneModel) return;
    setError(null);
    setNotice(null);
    const details = {
      name: name.trim() || null,
      productType: productType || undefined,
      brand: phoneModel.brand,
      model: phoneModel.model,
      phoneModelId: phoneModel.id,
      caseStyleId: caseStyleId || null,
      baseType: baseType || null,
    };
    const saved: GenerationJobView[] = [];
    let failed: Notice | null = null;
    for (const job of run.jobs.filter(settled)) {
      try {
        saved.push(await client.updateGenerationJob(job.id, details));
      } catch (e) {
        // 409: the view is no longer a settled mock-up (it moved on elsewhere); the rest still save.
        if (refusalOf(e) === "finished") continue;
        failed ??= { detail: refusalText(e, { admin, reference: job.id }), reference: job.id };
      }
    }
    onSaved(saved);
    if (failed) setError(failed);
    else if (saved.length === 0) setNotice("These images are still being made. Try again when they finish.");
    else onClose();
  };

  return (
    <Modal testid="mockup-edit-modal" title="Edit details" subtitle={runLabel(run)} onClose={onClose} footer={
      <ActionBar label="Edit details actions" primary={
        <BusyButton className="btn btn--primary" onAction={save} disabled={!phoneModel}
          data-testid="mockup-edit-save">Save</BusyButton>
      }>
        <button type="button" className="btn" onClick={onClose} data-testid="mockup-edit-cancel">Cancel</button>
      </ActionBar>
    }>
      {error && (
        <p className="banner banner--danger" role="alert" data-testid="mockup-edit-error">
          <ErrorNotice detail={error.detail} reference={error.reference} />
        </p>
      )}
      {notice && <p className="banner banner--warn" role="status" data-testid="mockup-edit-notice">{notice}</p>}
      <div className="generate-panel__details-grid">
        <ProductPickers idPrefix="mockup-edit" categoryTestId="mockup-edit-product-type"
          modelTestId="mockup-edit-model" retryTestId="mockup-edit-model-retry"
          categories={categories} category={productType} onCategory={setProductType}
          brand={brand} onBrand={(b) => { setBrand(b); if (phoneModel && phoneModel.brand.toLowerCase() !== b.toLowerCase()) setModelId(""); }}
          brandOptions={catalog.brands} allowAddBrand={false}
          modelId={modelId} onModel={(id) => { setModelId(id); if (!brand) setBrand(catalog.models.find((m) => m.id === id)?.brand ?? ""); }}
          models={catalog.optionsFor(brand)} modelsError={catalog.error} onRetryModels={() => void catalog.reload()} />
        <StylePicker value={caseStyleId} testIdBase="mockup-edit" allowAdd={false} onChange={setCaseStyleId} />
        <BaseTypePicker value={baseType} testIdBase="mockup-edit" onChange={setBaseType} />
        <label className="field" htmlFor="mockup-edit-name">
          <span className="label">Name / SKU</span>
          <input id="mockup-edit-name" className="input" type="text" value={name} maxLength={200} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CASE-PRO" data-testid="mockup-edit-name" />
        </label>
      </div>
    </Modal>
  );
}
